/**
 * Tableau workbooks still to move, per area. A dated snapshot, not live data: le-data Tableau harvest and
 * TS Events access counts for 2026-02-25 to 2026-08-25. Replace with a live feed or retire after the Tableau exit.
 */
export type AreaTableauWorkbook = { name: string; project: string; tableauLuid: string; opens6Months: number };

export type AreaTableauSnapshot = {
  /** Workbooks in the area's Tableau projects. Null when the area has no project of its own. */
  workbookCount: number | null;
  openedIn6Months: number;
  summary: string;
  top: readonly AreaTableauWorkbook[];
};

export const AREA_TABLEAU_SNAPSHOT_DATE = '2026-08-25';

export const AREA_TABLEAU_SNAPSHOT: Record<
  'commercial' | 'growth' | 'loyalty' | 'supply' | 'customer-service',
  AreaTableauSnapshot
> = {
  commercial: {
    workbookCount: 13,
    openedIn6Months: 10,
    summary: '13 workbooks in the Commercial and Core Dashboards projects. 10 were opened in the 6 months to 25 Aug.',
    top: [
      {
        name: 'Key Tracking Report',
        project: 'Core Dashboards',
        tableauLuid: 'b9982fe5-c59e-49ab-b417-9ca7bdb2562c',
        opens6Months: 6018,
      },
      {
        name: 'Commercial KPI Tracker',
        project: 'Commercial',
        tableauLuid: '40f1c8cc-23d3-41fb-a1ff-da50e64f662d',
        opens6Months: 2023,
      },
      {
        name: 'Experience Dash',
        project: 'Commercial',
        tableauLuid: 'f366677d-6a10-470e-97f6-d1361c86d86d',
        opens6Months: 1011,
      },
      {
        name: 'Accommodation Views, Impressions and Sales',
        project: 'Commercial',
        tableauLuid: '6b2ac6b7-28e8-48d4-87ea-e167b1bb6264',
        opens6Months: 132,
      },
    ],
  },
  growth: {
    workbookCount: 22,
    openedIn6Months: 11,
    summary: '22 workbooks in the Marketing project. 11 were opened in the 6 months to 25 Aug.',
    top: [
      {
        name: 'Performance Marketing',
        project: 'Marketing',
        tableauLuid: '13ed7134-958b-497c-9402-23e2abed06a4',
        opens6Months: 1359,
      },
      {
        name: 'Customer Comms Key Report',
        project: 'Marketing',
        tableauLuid: 'c1a9e444-d134-4ed2-8561-c3cacaf5ab60',
        opens6Months: 1350,
      },
      {
        name: 'Contribution Margin Tracker',
        project: 'Marketing',
        tableauLuid: '9a929674-6928-4d44-a54d-2f3381390060',
        opens6Months: 1005,
      },
      {
        name: 'Inspiration Blog Performance',
        project: 'Marketing',
        tableauLuid: '778b93eb-e55d-47ea-94f3-7a1095c504f8',
        opens6Months: 66,
      },
    ],
  },
  loyalty: {
    workbookCount: null,
    openedIn6Months: 12,
    summary:
      'Tableau has no Loyalty project. 12 loyalty workbooks opened in the 6 months to 25 Aug sit in 5 other projects.',
    top: [
      {
        name: 'LuxPlus+',
        project: '1 - Internal + Board',
        tableauLuid: '8cc8bd03-c961-4642-87af-3dad20353d12',
        opens6Months: 963,
      },
      {
        name: 'Loyalty Societe',
        project: 'Product',
        tableauLuid: '8d1a54ec-b387-4ccb-b494-124fc6ba5393',
        opens6Months: 451,
      },
      {
        name: 'Loyalty Societe Points',
        project: 'Product',
        tableauLuid: '28e6d678-1675-4208-a493-f8e835d2213c',
        opens6Months: 383,
      },
      { name: 'Loyalty', project: 'Product', tableauLuid: '363dbce3-eab9-43d8-8eb6-ddd508ff7d4a', opens6Months: 351 },
    ],
  },
  supply: {
    workbookCount: 12,
    openedIn6Months: 9,
    summary: '12 workbooks in the Supply project. 9 were opened in the 6 months to 25 Aug.',
    top: [
      {
        name: 'Vendor Report - Flash - Post Campaign',
        project: 'Supply',
        tableauLuid: 'c90a6de2-0c31-4b2c-af9b-4263316eea77',
        opens6Months: 6914,
      },
      {
        name: 'Product dash',
        project: 'Supply',
        tableauLuid: 'bfe0cc5e-9487-406e-8b7e-49df158068e3',
        opens6Months: 398,
      },
      {
        name: 'Deal Performance for Hotel',
        project: 'Supply',
        tableauLuid: '59f8e096-164d-44ab-a4f0-a4eb887a78d6',
        opens6Months: 272,
      },
      {
        name: 'Vendor Report - LPC - Post Campaign',
        project: 'Supply',
        tableauLuid: 'a4786a0c-662c-47a9-9d75-13b69494cbd1',
        opens6Months: 183,
      },
    ],
  },
  'customer-service': {
    workbookCount: 4,
    openedIn6Months: 3,
    summary: '4 workbooks in the Customer Service project. 3 were opened in the 6 months to 25 Aug.',
    top: [
      {
        name: 'CS Conversations & Sales MVP',
        project: 'Customer Service',
        tableauLuid: '55382544-3108-4bf9-ae95-bd6196504dc0',
        opens6Months: 456,
      },
      {
        name: 'Customer Interactions Report',
        project: 'Customer Service',
        tableauLuid: '8326613b-1c23-4f0d-9e01-6a0e705aa82d',
        opens6Months: 3,
      },
      {
        name: 'CSX Outcomes',
        project: 'Customer Service',
        tableauLuid: '7df8d877-ab33-43ec-9cb5-ad1c8ba71970',
        opens6Months: 2,
      },
      {
        name: 'CSO Actions - MVP v0.2',
        project: 'Customer Service',
        tableauLuid: 'acf1d0af-b754-4b1b-8e60-d120b04e972c',
        opens6Months: 0,
      },
    ],
  },
};

const normalise = (name: string) => name.trim().toLowerCase();

/**
 * The chips under "Still in Tableau". A workbook that already has a card above is dropped, and `more` counts
 * the workbooks the page shows no chip or card for. `more` is null for an area with no Tableau project.
 */
export function tableauChips(
  snapshot: AreaTableauSnapshot,
  cardTitles: readonly string[],
  limit: number,
): { chips: AreaTableauWorkbook[]; more: number | null } {
  const onCards = new Set(cardTitles.map(normalise));
  const open = snapshot.top.filter((workbook) => !onCards.has(normalise(workbook.name)));
  const chips = open.slice(0, limit);
  const covered = snapshot.top.length - open.length;
  const more = snapshot.workbookCount == null ? null : Math.max(0, snapshot.workbookCount - chips.length - covered);
  return { chips, more };
}
